import React, { useId, useRef } from "react"
import PropTypes from "prop-types"
import cx from "classnames"
import library from "./assets/icon-map"
import SocialIcon, { lookupSocialIcon } from "../social-icon"
import Tooltip from "../tooltip"
import VisuallyHidden from "../visually-hidden"
import log from "utils/log"
import { useStaticFeature } from "hooks/use-feature"

const SIZES = {
  sm: "w-4 h-4",
  md: "w-6 h-6",
  lg: "w-8 h-8"
}

const lookupIcon = iconName => library.find(i => i.name === iconName)

const useLogOnce = (event, data) => {
  const logEnabled = useStaticFeature("log_invalid_icons").enabled
  const loggedRef = useRef(false)
  if (logEnabled && !loggedRef.current) {
    log(event, { ...data, path: window.location.pathname, stack: new Error().stack })
    loggedRef.current = true
  }
}

const FallbackIcon = ({ icon, alt, name, ...props }) => {
  useLogOnce("fallback icon", { name, icon })

  const iconName = icon?.iconName ?? "FontAwesome"
  console.warn(
    `Use of ${iconName} icon is deprecated unless for social. Use social icons directly from SocialIcon component instead, or use the correct icon name from the component library's icon tokens`
  )
  const labelId = useId()

  return (
    <>
      <SocialIcon
        name={name}
        icon={icon}
        aria-labelledby={alt ? labelId : null}
        {...props}
      />
      {alt && <VisuallyHidden id={labelId}>{alt}</VisuallyHidden>}
    </>
  )
}

const UnknownIcon = ({ name }) => {
  useLogOnce("unknown icon", { name })

  const message = `The icon "${name}" you're trying to use doesn't exist. See available icons in the component library's icon tokens`
  const questionIcon = lookupIcon("question-circle")
  const QuestionIcon = questionIcon.component
  return (
    <Tooltip label={message}>
      <QuestionIcon
        className="text-red-600 animate-bounce"
        role="img"
      />
    </Tooltip>
  )
}

const Icon = ({ name, className, size = "md", alt, ...props }) => {
  className = cx(
    "fill-current inline-block overflow-visible",
    SIZES[size],
    className
  )
  const labelId = useId()

  // NOTE: I want to get rid of this entire fallback logic, I'm fairly sure it's no longer needed
  // but to be 100% sure I want to start logging it
  if (name && typeof name == "string") {
    const icon = lookupIcon(name)
    if (!icon) {
      const socialIcon = lookupSocialIcon(name)
      if (!socialIcon) {
        return <UnknownIcon name={name} />
      }
      return (
        <FallbackIcon
          icon={socialIcon}
          className={className}
          alt={alt}
          name={name}
          {...props}
          role="img"
        />
      )
    }

    const IconComponent = icon.component

    return (
      <>
        <IconComponent
          className={className}
          name={name}
          style={{ fontSize: "inherit" }}
          aria-labelledby={alt ? labelId : null}
          role="img"
          {...props}
        />
        {alt && <VisuallyHidden id={labelId}>{alt}</VisuallyHidden>}
      </>
    )
  }

  return <FallbackIcon icon={name} className={className} alt={alt} name={name} {...props} />
}

Icon.propTypes = {
  /** Matches name in Figma, using lowercase and hyphens instead of spaces. */
  name: PropTypes.string.isRequired,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  alt: PropTypes.string,
  /** Customize the icon. E.g. text-gray-700 */
  className: PropTypes.string
}

export default Icon
